/**
 * useDxfFiles — list the user's DXF/DWG library.
 *
 * v1 ref: ``ando-frontend/app/pages/DxfLibraryPage.tsx::loadFiles`` (1847
 * LoC monolith). v2 splits the concerns:
 *   - this hook owns the paginated list query
 *   - ``useDxfCategories`` (sibling) owns the categories sidebar
 *   - ``useDxfMutations`` owns delete / metadata-update / category-rename
 *
 * Polling cadence: 30s while there's at least one ``pending`` or
 * ``processing`` row (so the user sees ingestion progress), otherwise
 * 5 min (stale view is fine). Implemented via ``refetchInterval`` as a
 * function — TanStack Query supports that.
 */

import { queryOptions, useQuery } from "@tanstack/react-query";
import {
  listDxfFiles,
  type DxfFile,
  type ListDxfFilesParams,
} from "@ando/core";

export function dxfFilesQueryKey(
  params: ListDxfFilesParams,
): readonly unknown[] {
  return [
    "dxf",
    "files",
    params.category ?? null,
    params.status ?? null,
    params.search ?? null,
    params.limit ?? null,
    params.offset ?? null,
  ] as const;
}

export interface UseDxfFilesOptions {
  readonly enabled?: boolean;
  readonly params?: ListDxfFilesParams;
}

export interface UseDxfFilesResult {
  readonly files: readonly DxfFile[];
  readonly total: number;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly refetch: () => Promise<unknown>;
}

const DEFAULT_PARAMS: ListDxfFilesParams = { limit: 50, offset: 0 };

function hasInFlightFile(files: readonly DxfFile[]): boolean {
  return files.some(
    (f) => f.status === "pending" || f.status === "processing",
  );
}

/**
 * Shared list query — hook + `/workspace/technical` route loader (intent
 * preload). Applies the same `DEFAULT_PARAMS` merge so the loader warms the
 * EXACT key the component reads (e.g. `{ limit: 200 }` → default category).
 */
export const dxfFilesQueryOptions = (options: Pick<UseDxfFilesOptions, "params"> = {}) => {
  const params = { ...DEFAULT_PARAMS, ...(options.params ?? {}) };
  return queryOptions({
    queryKey: dxfFilesQueryKey(params),
    queryFn: () => listDxfFiles(params),
    staleTime: 30_000,
    refetchInterval: (q) => {
      const files = q.state.data?.files ?? [];
      return hasInFlightFile(files) ? 30_000 : 5 * 60_000;
    },
  });
};

export function useDxfFiles(
  options: UseDxfFilesOptions = {},
): UseDxfFilesResult {
  const enabled = options.enabled ?? true;

  const query = useQuery({
    ...dxfFilesQueryOptions({ params: options.params }),
    enabled,
  });

  return {
    files: query.data?.files ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
  };
}
